import { Injectable } from '@angular/core';
import { Dict, jsonOrderedStringify, UnpackedObservable } from '@nk/utils-lib';
import { concat, merge, Observable, of, Subscription } from 'rxjs';
import { share, tap } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CacheableFunction<Args extends unknown[] = any[], ReturnEntity = unknown> = (
  ...args: Args
) => Observable<ReturnEntity>;

type CustomParams2Key = ((...keys: unknown[]) => string) | null;

const DEFAULT_MAX_AGE = 5 * 60 * 1000;

export interface CacheableObject {
  toCachingKey(): string;
}

function isCacheableObject<T>(item: unknown): item is T & CacheableObject {
  return !!item && 'toCachingKey' in (item as CacheableObject);
}

@Injectable()
export class CachingService {
  public caches: LocalCache<unknown, CacheableFunction>[] = [];

  private _debug = false;
  public get debug(): boolean {
    return this._debug;
  }

  public set debug(value: boolean) {
    this._debug = value;
    for (const cache of this.caches) {
      cache.debug = value;
    }
  }

  private _disable = false;
  public get disable(): boolean {
    return this._disable;
  }

  public set disable(value: boolean) {
    this._disable = value;
    for (const cache of this.caches) {
      cache.disable = value;
    }
  }

  public create<ClassName, CachedFunction extends CacheableFunction>(
    context: ClassName,
    observableFactory: CachedFunction,
    share = true,
    maxAge = 5 * 60 * 1000,
    customParams2Key: CustomParams2Key = null,
  ): LocalCache<ClassName, CachedFunction> {
    const cache = new LocalCache<ClassName, CachedFunction>(
      context,
      observableFactory,
      share,
      maxAge,
      this.disable,
      this.debug,
      customParams2Key,
    );
    this.caches.push(cache);
    return cache;
  }

  public invalidateAllCache(): void {
    for (const cache of this.caches) {
      cache.invalidateAllCache();
    }
  }
}

class CacheElement<T> {
  constructor(
    public expiry: number,
    public payload: T,
  ) {}
}

export class LocalCache<ClassName, CachedFunction extends CacheableFunction> {
  private _cacheName = 'UNKNOWN';
  private _cache: Dict<CacheElement<unknown>> = new Dict<CacheElement<unknown>>();
  private _inflight: Dict<ReturnType<CachedFunction>> = new Dict<ReturnType<CachedFunction>>();

  constructor(
    private _context: ClassName,
    private _observableFactory: CachedFunction,
    private _share = true,
    private readonly _maxAge = DEFAULT_MAX_AGE,
    public disable = false,
    public debug = false,
    private _customParams2Key: CustomParams2Key = null,
  ) {
    if (this._context && this._context.constructor) {
      this._cacheName = this._context.constructor.name + '.' + _observableFactory.name;
    } else {
      this._cacheName = _observableFactory.name;
    }
  }

  public get(...keys: Parameters<CachedFunction>): ReturnType<CachedFunction> {
    if (this.disable) {
      return this.bypass(...keys);
    }
    const key = this.params2key(...keys);
    const existingCacheElement = this._cache[key];
    const expiry = Date.now() + this._maxAge;

    if (!existingCacheElement) {
      let nextObservable = this._inflight[key];
      this._log(
        `%cMaking new cache of ${this._cacheName}: ${key}`,
        'background-color: orange; color: white; padding:5px',
      );
      if (nextObservable) {
        this._log(
          `%cGet is already in flight on ${this._cacheName}: ${key}`,
          'background-color: blue; color: white; padding:5px',
        );
        return nextObservable;
      } else {
        const factoryOutput = this._useObservableFactory(...keys);
        if (!factoryOutput) {
          throw new Error(
            `Factory provided to cache did not return an observable!
            Did you remember to return an observable
            (for all branches) in ${this._cacheName}?`,
          );
        }
        nextObservable = factoryOutput.pipe(
          this._share ? share() : tap(),
          tap((payload) => {
            this._cache[key] = new CacheElement(expiry, payload);
            delete this._inflight[key];
          }),
        ) as ReturnType<CachedFunction>;
        this._inflight[key] = nextObservable;
        return nextObservable;
      }
    } else if (existingCacheElement.expiry < Date.now()) {
      this._log(
        `%cCache expired on ${this._cacheName}. Make new inflight: ${key}`,
        'background-color: red; color: white; padding:5px',
      );
      let nextObservable = this._inflight[key];
      if (nextObservable) {
        return nextObservable;
      } else {
        nextObservable = this._useObservableFactory(...keys).pipe(
          this._share ? share() : tap(),
          tap((payload) => {
            this._cache[key] = new CacheElement(expiry, payload);
            delete this._inflight[key];
          }),
        ) as ReturnType<CachedFunction>;
        this._inflight[key] = nextObservable;
        return nextObservable;
      }
    } else {
      this._log(
        `%cGetting ${this._cacheName} from cache ${key}`,
        'background-color: #55d41d; color: #ffffff; padding:5px',
      );
      const nextObservable = this._inflight[key];
      if (nextObservable) {
        return nextObservable;
      } else {
        return of(this._cache[key].payload) as ReturnType<CachedFunction>;
      }
    }
  }

  public fetch(...keys: Parameters<CachedFunction>): ReturnType<CachedFunction> {
    if (this.disable) {
      return this.bypass(...keys);
    }

    this.invalidate(...keys);
    return this.get(...keys);
  }

  public snapshot(...keys: Parameters<CachedFunction>): UnpackedObservable<ReturnType<CachedFunction>> | null {
    // Get whatever value is cached.
    const key = this.params2key(...keys);
    const cacheElement = this._cache[key];
    if (cacheElement) {
      return cacheElement.payload as UnpackedObservable<ReturnType<CachedFunction>>;
    } else {
      return null;
    }
  }

  public heat(...keys: Parameters<CachedFunction>): Subscription {
    if (!this.disable) {
      return this.get(...keys).subscribe();
    } else {
      return new Subscription();
    }
  }

  public fastHeatMany(keySet: Parameters<CachedFunction>[]): Subscription {
    if (!this.disable) {
      return merge(...keySet.map((keys) => this.get(...keys))).subscribe();
    } else {
      return new Subscription();
    }
  }

  public slowHeatMany(keySet: Parameters<CachedFunction>[]): Subscription {
    if (!this.disable) {
      return concat(...keySet.map((keys) => this.get(...keys))).subscribe();
    } else {
      return new Subscription();
    }
  }

  public bypass(...keys: Parameters<CachedFunction>): ReturnType<CachedFunction> {
    // Get data from source bypassing cache entirely.
    return this._useObservableFactory(...keys);
  }

  /*
   * Invalidates cache only for provided keys, forcing a fetch on next load.
   */
  public invalidate(...keys: Parameters<CachedFunction>): void {
    if (!this.disable) {
      const key = this.params2key(...keys);
      const cacheElement = this._cache[key];
      if (cacheElement) {
        cacheElement.expiry = 0;
      }
    } else {
      this._log('invalidation is disabled');
    }
  }

  /*
   * Invalidates cache for entire endpoint, forcing a fetch on next loads. Warning: might be expensive operation.
   */
  public invalidateAllCache(): void {
    if (!this.disable) {
      const keys = Object.keys(this._cache);
      for (const key of keys) {
        this._cache[key].expiry = 0;
      }
    } else {
      this._log('invalidation is disabled');
    }
  }

  private _getObjectParamStringified(param: unknown): string | never {
    if (typeof param === 'object' && param !== null) {
      return jsonOrderedStringify(param);
    }
    throw new Error('Error making a cache key from param');
  }

  protected params2key(...params: Parameters<CachedFunction>): string {
    if (this._customParams2Key) {
      return this._customParams2Key(params);
    } else {
      return params
        .map((param) => (param === null || param === undefined ? '' : param))
        .map((param) => {
          if (typeof param === 'number' || typeof param === 'string' || typeof param === 'boolean') {
            return param.toString();
          } else {
            try {
              if (isCacheableObject(param)) {
                return param.toCachingKey();
              }
              return this._getObjectParamStringified(param);
            } catch (ex) {
              console.error('YOU SHALL NOT PASS objects with functions into the cache.');
              throw ex;
            }
          }
        })
        .join('^^^');
    }
  }

  private _useObservableFactory(...keys: Parameters<CachedFunction>): ReturnType<CachedFunction> {
    try {
      if (this._context) {
        return this._observableFactory.call(this._context, ...keys) as ReturnType<CachedFunction>;
      } else {
        return this._observableFactory(...keys) as ReturnType<CachedFunction>;
      }
    } catch (error) {
      console.error(
        'Your cache keys might be wrong. Check that the keys you are using have the correct number of arguments.',
      );
      throw error;
    }
  }

  public toString(): string {
    return this._observableFactory.toString();
  }

  private _log(...message: string[]): void {
    if (this.debug) {
      console.info(...message);
    }
  }
}
